// City metadata för alla city×hub-sidor.
// Används för att generera filmbyra/videobyra/produktionsbolag-routes,
// LocalBusiness-schema, breadcrumbs och RelatedHubs-länkar.

export interface CityMeta {
  /** URL-slug (a-z, inga åäö, inga mellanslag) */
  slug: string;
  /** Visningsnamn med rätt diakritiska tecken */
  name: string;
  /** Län eller region för LocalBusiness postalRegion */
  region: string;
  /** Längre region-namn för copy (t.ex. "Öresundsregionen", "Västsverige") */
  regionLong: string;
  /** Närliggande orter som ofta ingår i samma upptagningsområde */
  neighbors: string[];
  /** Branscher som är överrepresenterade i staden — driver bullet/section-copy */
  industries: string[];
  /** Kort styrkebeskrivning av staden — används i bullet 1 */
  edge: string;
  /** Sätts för stadsdelar/närområden: namnet på moderstaden. */
  partOf?: string;
}

export const CITIES: CityMeta[] = [
  {
    slug: "stockholm",
    name: "Stockholm",
    region: "Stockholms län",
    regionLong: "Stockholm och Mälardalen",
    neighbors: ["Solna", "Sundbyberg", "Täby", "Nacka"],
    industries: ["SaaS", "fintech", "konsumentvarumärken", "B2B-tech"],
    edge: "Vårt huvudkontor och största team — full kapacitet utan resa.",
  },
  {
    slug: "goteborg",
    name: "Göteborg",
    region: "Västra Götalands län",
    regionLong: "Västsverige",
    neighbors: ["Mölndal", "Borås", "Trollhättan", "Lindholmen"],
    industries: ["industri", "fastighet", "life science", "tech-scale-ups"],
    edge: "Hemmaplan för västsvensk industri — vi kan produktionsmiljöer och säkerhetsrutiner.",
  },
  {
    slug: "malmo",
    name: "Malmö",
    region: "Skåne län",
    regionLong: "Öresundsregionen",
    neighbors: ["Lund", "Helsingborg", "Köpenhamn"],
    industries: ["SaaS", "life science", "konsumentvarumärken", "deeptech"],
    edge: "Bilateral produktion mot Köpenhamn när affären spänner över Öresund.",
  },
  {
    slug: "uppsala",
    name: "Uppsala",
    region: "Uppsala län",
    regionLong: "norra Mälardalen",
    neighbors: ["Knivsta", "Enköping", "Sigtuna"],
    industries: ["universitet", "life science", "MedTech", "tech-startups"],
    edge: "Stark tradition av forskningsdriven kommunikation — vi översätter komplex teknik till säljande film.",
  },
  {
    slug: "linkoping",
    name: "Linköping",
    region: "Östergötlands län",
    regionLong: "Östergötland",
    neighbors: ["Norrköping", "Mjölby", "Motala"],
    industries: ["försvarsindustri", "tech", "universitet", "tillverkning"],
    edge: "Tunga referenser inom försvar och tech — vi hanterar sekretess och tekniska briefer.",
  },
  {
    slug: "orebro",
    name: "Örebro",
    region: "Örebro län",
    regionLong: "Bergslagen och Mellansverige",
    neighbors: ["Karlskoga", "Kumla", "Hallsberg"],
    industries: ["logistik", "tillverkning", "handel", "offentlig sektor"],
    edge: "Centralt geografiskt läge — Stockholm, Göteborg och Malmö nås på en dag.",
  },
  {
    slug: "vasteras",
    name: "Västerås",
    region: "Västmanlands län",
    regionLong: "Mälardalen",
    neighbors: ["Enköping", "Köping", "Sala"],
    industries: ["energi", "industri", "tech", "tillverkning"],
    edge: "ABB-staden — vi har lång erfarenhet av tung industri och säkerhetskrav.",
  },
  {
    slug: "jonkoping",
    name: "Jönköping",
    region: "Jönköpings län",
    regionLong: "Småland",
    neighbors: ["Huskvarna", "Vaggeryd", "Värnamo"],
    industries: ["tillverkning", "logistik", "handel", "familjeföretag"],
    edge: "Småländskt entreprenörskap — vi pratar samma språk som ägarledda bolag.",
  },
  {
    slug: "helsingborg",
    name: "Helsingborg",
    region: "Skåne län",
    regionLong: "norra Skåne och Öresund",
    neighbors: ["Lund", "Landskrona", "Ängelholm"],
    industries: ["logistik", "handel", "FMCG", "industri"],
    edge: "Logistik-nav mellan Sverige och kontinenten — vi producerar i hela Öresund.",
  },
  {
    slug: "umea",
    name: "Umeå",
    region: "Västerbottens län",
    regionLong: "Norrland",
    neighbors: ["Skellefteå", "Sundsvall", "Luleå"],
    industries: ["universitet", "green tech", "skog och energi", "MedTech"],
    edge: "Norra Sveriges tech-hubb — vi har gjort film för Northvolt-ekosystemet och regionens green tech.",
  },
  {
    slug: "lund",
    name: "Lund",
    region: "Skåne län",
    regionLong: "Skåne",
    neighbors: ["Malmö", "Lomma", "Staffanstorp"],
    industries: ["universitet", "life science", "deeptech", "SaaS"],
    edge: "Forskningsdriven kommunikation — vi har erfarenhet av Medicon Village och Lunds tech-scen.",
  },
  {
    slug: "solna",
    name: "Solna",
    region: "Stockholms län",
    regionLong: "Stockholm",
    neighbors: ["Stockholm", "Sundbyberg", "Sollentuna"],
    industries: ["healthcare", "biotech", "B2B-tech", "konsumentvarumärken"],
    edge: "Karolinska-närhet och stark life science-koncentration — vi gör avancerad medtech-film.",
  },
  {
    slug: "sundsvall",
    name: "Sundsvall",
    region: "Västernorrlands län",
    regionLong: "södra Norrland",
    neighbors: ["Härnösand", "Timrå", "Östersund"],
    industries: ["bank och försäkring", "skog", "energi", "offentlig sektor"],
    edge: "Norrlands finanscentrum — vi hanterar regulatoriskt känsligt content och bank-tonalitet.",
  },
  {
    slug: "karlstad",
    name: "Karlstad",
    region: "Värmlands län",
    regionLong: "Värmland",
    neighbors: ["Kristinehamn", "Säffle", "Arvika"],
    industries: ["skog", "papper och pappersmassa", "handel", "tech"],
    edge: "Värmlands ledande tech- och universitetsstad — vi gör allt från konsument till industri.",
  },
  {
    slug: "vaxjo",
    name: "Växjö",
    region: "Kronobergs län",
    regionLong: "Småland",
    neighbors: ["Alvesta", "Älmhult", "Kalmar"],
    industries: ["möbel och inredning", "green tech", "universitet", "skog"],
    edge: "Sveriges grönaste stad — vi har vana av hållbarhetskommunikation som faktiskt går igenom.",
  },
  {
    slug: "halmstad",
    name: "Halmstad",
    region: "Hallands län",
    regionLong: "Halland och västra Sverige",
    neighbors: ["Falkenberg", "Varberg", "Laholm"],
    industries: ["industri", "handel", "turism", "tech"],
    edge: "Halländsk mellanstad med stark industri — vi pendlar smidigt mellan Gbg och Skåne.",
  },
  {
    slug: "gavle",
    name: "Gävle",
    region: "Gävleborgs län",
    regionLong: "Gästrikland",
    neighbors: ["Sandviken", "Söderhamn", "Hudiksvall"],
    industries: ["logistik", "stål", "skog", "offentlig sektor"],
    edge: "Strategiskt läge norr om Stockholm — vi servar både region och nationella varumärken.",
  },
  {
    slug: "eskilstuna",
    name: "Eskilstuna",
    region: "Södermanlands län",
    regionLong: "Mälardalen",
    neighbors: ["Strängnäs", "Västerås", "Södertälje"],
    industries: ["tillverkning", "stål", "logistik", "tech"],
    edge: "Mälardalens industri-hjärta — vi kan tung tillverkning och B2B-tonalitet.",
  },
  {
    slug: "norrkoping",
    name: "Norrköping",
    region: "Östergötlands län",
    regionLong: "Östergötland",
    neighbors: ["Linköping", "Söderköping", "Finspång"],
    industries: ["logistik", "datacenter", "industri", "kreativa näringar"],
    edge: "Sveriges datacenter-stad och växande kreativ scen — vi gör både teknisk och varumärkesfilm.",
  },
  {
    slug: "boras",
    name: "Borås",
    region: "Västra Götalands län",
    regionLong: "Sjuhärad",
    neighbors: ["Ulricehamn", "Mark", "Bollebygd"],
    industries: ["textil och mode", "e-handel", "logistik", "tillverkning"],
    edge: "Sveriges textilhuvudstad — vi har case inom mode, e-handel och svensk tillverkning.",
  },
  {
    slug: "lulea",
    name: "Luleå",
    region: "Norrbottens län",
    regionLong: "Norrbotten",
    neighbors: ["Boden", "Piteå", "Kiruna"],
    industries: ["stål", "green tech", "datacenter", "universitet"],
    edge: "Centrum för norra Sveriges gröna omställning — vi gör film för fossilfri stål och nya tech-investeringar.",
  },
  {
    slug: "kalmar",
    name: "Kalmar",
    region: "Kalmar län",
    regionLong: "sydöstra Sverige",
    neighbors: ["Nybro", "Oskarshamn", "Öland"],
    industries: ["energi", "tillverkning", "besöksnäring", "universitet"],
    edge: "Stark bas inom energi och tillverkning — vi filmar produktionsmiljöer med rätt säkerhetsrutiner.",
  },
  {
    slug: "skovde",
    name: "Skövde",
    region: "Västra Götalands län",
    regionLong: "Skaraborg",
    neighbors: ["Skara", "Falköping", "Mariestad"],
    industries: ["fordonsindustri", "tillverkning", "spelutveckling", "försvar"],
    edge: "Fordonsindustri och spelkluster i samma stad — vi växlar mellan fabriksgolv och studio.",
  },
  {
    slug: "trollhattan",
    name: "Trollhättan",
    region: "Västra Götalands län",
    regionLong: "Fyrbodal",
    neighbors: ["Vänersborg", "Uddevalla", "Lilla Edet"],
    industries: ["fordonsindustri", "film och media", "energi", "tillverkning"],
    edge: "Trollywood-arvet gör att lokal crew och locations finns nära.",
  },
  {
    slug: "uddevalla",
    name: "Uddevalla",
    region: "Västra Götalands län",
    regionLong: "Bohuslän",
    neighbors: ["Trollhättan", "Munkedal", "Stenungsund"],
    industries: ["marin industri", "handel", "logistik", "besöksnäring"],
    edge: "Bohuskusten ger locations som få andra orter kan matcha på en produktionsdag.",
  },
  {
    slug: "varberg",
    name: "Varberg",
    region: "Hallands län",
    regionLong: "Halland",
    neighbors: ["Falkenberg", "Kungsbacka", "Halmstad"],
    industries: ["energi", "handel", "besöksnäring", "tillverkning"],
    edge: "Mellan Göteborg och Malmö — vi når er utan reseavdrag från båda hållen.",
  },
  {
    slug: "kristianstad",
    name: "Kristianstad",
    region: "Skåne län",
    regionLong: "nordöstra Skåne",
    neighbors: ["Hässleholm", "Åhus", "Bromölla"],
    industries: ["livsmedel", "tillverkning", "offentlig sektor", "logistik"],
    edge: "Livsmedels- och tillverkningskluster — vi kan hygienzoner och produktionslinor.",
  },
  {
    slug: "karlskrona",
    name: "Karlskrona",
    region: "Blekinge län",
    regionLong: "Blekinge",
    neighbors: ["Ronneby", "Karlshamn", "Emmaboda"],
    industries: ["telekom", "marin teknik", "försvar", "universitet"],
    edge: "Telekom och marin teknik — vana vid sekretess och tekniska briefer.",
  },
  {
    slug: "nykoping",
    name: "Nyköping",
    region: "Södermanlands län",
    regionLong: "Sörmland",
    neighbors: ["Oxelösund", "Katrineholm", "Trosa"],
    industries: ["tillverkning", "logistik", "besöksnäring", "offentlig sektor"],
    edge: "En timme från Stockholm — storstadsteam till lokalt pris.",
  },
  {
    slug: "sodertalje",
    name: "Södertälje",
    region: "Stockholms län",
    regionLong: "södra Storstockholm",
    neighbors: ["Nykvarn", "Salem", "Botkyrka"],
    industries: ["fordonsindustri", "life science", "logistik", "tillverkning"],
    edge: "Tunga industri- och life science-miljöer med hög säkerhetsnivå — vi har rutinerna.",
  },
  {
    slug: "falun",
    name: "Falun",
    region: "Dalarnas län",
    regionLong: "Dalarna",
    neighbors: ["Borlänge", "Säter", "Leksand"],
    industries: ["besöksnäring", "stål", "offentlig sektor", "tillverkning"],
    edge: "Världsarv och industri i samma stad — starka locations året runt.",
  },
  {
    slug: "borlange",
    name: "Borlänge",
    region: "Dalarnas län",
    regionLong: "Dalarna",
    neighbors: ["Falun", "Säter", "Ludvika"],
    industries: ["stål", "logistik", "tillverkning", "energi"],
    edge: "Stålindustrins hjärta i Dalarna — vi filmar i tunga produktionsmiljöer.",
  },
  {
    slug: "ostersund",
    name: "Östersund",
    region: "Jämtlands län",
    regionLong: "Jämtland och Härjedalen",
    neighbors: ["Åre", "Krokom", "Brunflo"],
    industries: ["besöksnäring", "tech", "livsmedel", "offentlig sektor"],
    edge: "Norra Sveriges besöksnäring — och en tech-scen som växer snabbt.",
  },
  {
    slug: "visby",
    name: "Visby",
    region: "Gotlands län",
    regionLong: "Gotland",
    neighbors: ["Slite", "Hemse", "Klintehamn"],
    industries: ["besöksnäring", "livsmedel", "offentlig sektor", "event"],
    edge: "Vi tar med komplett rigg till ön — inga överraskningar i logistiken.",
  },
  {
    slug: "skelleftea",
    name: "Skellefteå",
    region: "Västerbottens län",
    regionLong: "Västerbotten",
    neighbors: ["Umeå", "Piteå", "Boliden"],
    industries: ["green tech", "batteriindustri", "gruvnäring", "bygg"],
    edge: "Batteriboomens epicentrum — vi gör film för bolag som rekryterar i stor skala.",
  },
  {
    slug: "kista",
    name: "Kista",
    region: "Stockholms län",
    regionLong: "norra Storstockholm",
    neighbors: ["Sundbyberg", "Solna", "Sollentuna"],
    industries: ["B2B-tech", "telekom", "SaaS", "IT-konsult"],
    edge: "Sveriges tätaste tech-kluster — vi kan språket i B2B-tech.",
    partOf: "Stockholm",
  },
  {
    slug: "sundbyberg",
    name: "Sundbyberg",
    region: "Stockholms län",
    regionLong: "norra Storstockholm",
    neighbors: ["Solna", "Bromma", "Kista"],
    industries: ["SaaS", "konsult", "handel", "fastighet"],
    edge: "Tio minuter från city — enkel logistik och gott om kontorslocations.",
    partOf: "Stockholm",
  },
  {
    slug: "nacka",
    name: "Nacka",
    region: "Stockholms län",
    regionLong: "östra Storstockholm",
    neighbors: ["Sickla", "Värmdö", "Stockholm"],
    industries: ["fastighet", "handel", "tech", "bygg"],
    edge: "Sickla och Nacka strand ger industriell karaktär nära innerstaden.",
    partOf: "Stockholm",
  },
  {
    slug: "taby",
    name: "Täby",
    region: "Stockholms län",
    regionLong: "nordöstra Storstockholm",
    neighbors: ["Danderyd", "Vallentuna", "Sollentuna"],
    industries: ["handel", "fastighet", "vård", "konsult"],
    edge: "Nordost om stan — vi rullar in med full rigg utan restidsavdrag.",
    partOf: "Stockholm",
  },
  {
    slug: "sodermalm",
    name: "Södermalm",
    region: "Stockholms län",
    regionLong: "centrala Stockholm",
    neighbors: ["Hammarby Sjöstad", "Gamla stan", "Årsta"],
    industries: ["SaaS", "kreativ sektor", "konsumentvarumärken", "e-handel"],
    edge: "Innerstadslocations och studio inom cykelavstånd från vårt kontor.",
    partOf: "Stockholm",
  },
  {
    slug: "molndal",
    name: "Mölndal",
    region: "Västra Götalands län",
    regionLong: "Storgöteborg",
    neighbors: ["Göteborg", "Kungsbacka", "Partille"],
    industries: ["life science", "tillverkning", "fastighet", "tech"],
    edge: "Life science-kluster i AstraZenecas grannskap — vi kan lab- och renrumsmiljöer.",
    partOf: "Göteborg",
  },
  {
    slug: "hisingen",
    name: "Hisingen",
    region: "Västra Götalands län",
    regionLong: "Storgöteborg",
    neighbors: ["Göteborg", "Kungälv", "Partille"],
    industries: ["fordonsindustri", "hamn och logistik", "tillverkning", "tech"],
    edge: "Fordonsindustri och hamn — tunga miljöer med säkerhetskrav vi kan.",
    partOf: "Göteborg",
  },
  {
    slug: "lindholmen",
    name: "Lindholmen",
    region: "Västra Götalands län",
    regionLong: "Storgöteborg",
    neighbors: ["Hisingen", "Göteborg", "Mölndal"],
    industries: ["mobility-tech", "forskning", "SaaS", "fordonsindustri"],
    edge: "Science park-miljön ger tech-referenser och färdiga inspelningsmiljöer.",
    partOf: "Göteborg",
  },
  {
    slug: "hyllie",
    name: "Hyllie",
    region: "Skåne län",
    regionLong: "Malmö och Öresund",
    neighbors: ["Malmö", "Limhamn", "Köpenhamn"],
    industries: ["fastighet", "event", "handel", "tech"],
    edge: "Nybyggd stadsdel med rena linjer — och tio minuter till Kastrup.",
    partOf: "Malmö",
  },
];

/** Slå upp city-metadata på slug. */
export function getCity(slug: string): CityMeta | undefined {
  return CITIES.find((c) => c.slug === slug);
}

/** Alla city-slugs (a-z). */
export const CITY_SLUGS = CITIES.map((c) => c.slug);
